/**
 * /operations/{id} — карточка тех. операции (AK-525).
 *
 * Мёртвый роут: список операций и виджет карточки поля вели на `/operations/{id}`,
 * которого в роутере не существовало — клик по операции упирался в 404.
 * Здесь появляется сама страница и, главное, ЕДИНСТВЕННОЕ в вебе место, где
 * операцией можно управлять: сменить статус, поправить план/факт, вести работы
 * (кто, на чём, когда, сколько га).
 *
 * Layout — `PageWithRail`: слева работы (главный рабочий объект), справа
 * паспорт операции + действия статуса.
 */
import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { AlertCircle, Pencil, Plus, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { OpStatusBadge } from '@/components/operations/OpStatusBadge';
import { OperationStatusActions } from '@/components/operations/OperationStatusActions';
import { OperationFormDialog } from '@/components/operations/OperationFormDialog';
import { WorkFormDialog } from '@/components/operations/WorkFormDialog';
import {
  useCompanyOpTypes,
  useCreateWork,
  useArchiveWork,
  useOperation,
  useOperationWorks,
  useUpdateOperation,
  useUpdateWork,
  type Work,
} from '@/components/operations/operationsApi';
import { lifecycleLabel } from '@/components/operations/opLifecycle';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageWithRail } from '@/components/ui/page-with-rail';
import { Skeleton } from '@/components/ui/skeleton';
import { PERM, useCan } from '@/lib/auth/usePermissions';
import { formatRuDate } from '@/lib/dateFmt';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-10 items-center justify-between gap-4 border-b border-border py-2 text-sm leading-6 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{children}</span>
    </div>
  );
}

function areaText(v?: number | null): string {
  return v != null ? `${v.toFixed(1)} га` : '—';
}

export function OperationDetailPage() {
  const { opId } = useParams({ strict: false }) as { opId: string };
  const canWrite = useCan(PERM.operationWrite, 'u');
  const canCreate = useCan(PERM.operationWrite, 'c');
  const canDelete = useCan(PERM.operationWrite, 'd');

  const { data: op, isLoading, isError, error } = useOperation(opId);
  const { data: works } = useOperationWorks(opId);
  const { data: opTypes } = useCompanyOpTypes();

  const updateOp = useUpdateOperation(opId, op?.field_id);
  const createWork = useCreateWork(opId, op?.field_id);
  const updateWork = useUpdateWork(opId, op?.field_id);
  const archiveWork = useArchiveWork(opId, op?.field_id);

  const [editOpen, setEditOpen] = useState(false);
  const [workDialog, setWorkDialog] = useState<{ open: boolean; work: Work | null }>({
    open: false,
    work: null,
  });

  if (isLoading) {
    return (
      <AppShell>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (isError || !op) {
    return (
      <AppShell>
        <div className="flex flex-col gap-4">
          <PageHeader title="Операция" />
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error?.message ?? 'Операция не найдена.'}{' '}
              <Link to="/operations" className="underline">
                К списку операций
              </Link>
            </AlertDescription>
          </Alert>
        </div>
      </AppShell>
    );
  }

  const workRows = works ?? [];
  const busy = updateOp.isPending;
  const workSaving = createWork.isPending || updateWork.isPending;
  const workError =
    (createWork.error?.message || updateWork.error?.message) ?? null;

  const submitWork = (body: Parameters<typeof createWork.mutate>[0]) => {
    const editing = workDialog.work;
    const done = () => setWorkDialog({ open: false, work: null });
    if (editing) updateWork.mutate({ workId: editing.id, body }, { onSuccess: done });
    else createWork.mutate(body, { onSuccess: done });
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-4">
        <PageHeader
          title={op.name}
          breadcrumbs={[
            { label: 'Тех. операции', to: '/operations' },
            { label: op.name },
          ]}
        />

        <PageWithRail
          rail={
            <>
              <Card>
                <CardHeader className="p-4 pb-3">
                  <CardTitle>Статус</CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <OpStatusBadge
                        status={op.display_status}
                        rescheduled={op.was_rescheduled}
                      />
                      <span className="text-xs text-muted-foreground">
                        {lifecycleLabel(op.lifecycle_state)}
                      </span>
                    </div>
                    <OperationStatusActions
                      lifecycleState={op.lifecycle_state}
                      allowedStates={op.allowed_lifecycle_states}
                      busy={busy}
                      canWrite={canWrite}
                      onTransition={(target) =>
                        updateOp.mutate({ lifecycle_state: target })
                      }
                    />
                    {updateOp.isError && (
                      <Alert variant="destructive">
                        <AlertDescription>
                          {updateOp.error?.message}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-4 pb-3">
                  <div className="flex items-center gap-2">
                    <CardTitle>Операция</CardTitle>
                    {canWrite && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto"
                        onClick={() => setEditOpen(true)}
                      >
                        <Pencil className="h-4 w-4" />
                        Править
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <Row label="Поле">
                    <Link
                      to={`/fields/${op.field_id}` as string}
                      className="text-primary hover:underline"
                    >
                      {op.field_name ?? 'Поле'}
                    </Link>
                  </Row>
                  <Row label="Тип">{op.op_type_local_name ?? '—'}</Row>
                  <Row label="Культура">{op.crop_name ?? '—'}</Row>
                  <Row label="План">
                    {formatRuDate(op.planned_start_date) || '—'}
                    {op.planned_end_date ? ` — ${formatRuDate(op.planned_end_date)}` : ''}
                  </Row>
                  <Row label="Факт">
                    {formatRuDate(op.actual_start_date) || '—'}
                    {op.actual_end_date ? ` — ${formatRuDate(op.actual_end_date)}` : ''}
                  </Row>
                  <Row label="Площадь план / факт">
                    {areaText(op.planned_area_ha)} / {areaText(op.actual_area_ha)}
                  </Row>
                  <Row label="Сделано работами">
                    {areaText(op.works_total_area_ha)}
                  </Row>
                  {op.comment && <Row label="Комментарий">{op.comment}</Row>}
                </CardContent>
              </Card>
            </>
          }
        >
          <Card>
            <CardHeader className="p-4 pb-3">
              <div className="flex items-center gap-2">
                <CardTitle>Работы</CardTitle>
                <span className="text-xs text-muted-foreground">
                  {workRows.length > 0 ? `${workRows.length} шт.` : ''}
                </span>
                {canCreate && (
                  <Button
                    size="sm"
                    className="ml-auto"
                    onClick={() => setWorkDialog({ open: true, work: null })}
                  >
                    <Plus className="h-4 w-4" />
                    Добавить работу
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              {workRows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  Работ пока нет. Добавьте первую — операция сама перейдёт в
                  статус «выполняется».
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {workRows.map((w) => (
                    <li key={w.id} className="flex min-h-10 items-center gap-3 py-2">
                      <span className="w-24 shrink-0 text-xs tabular-nums text-muted-foreground">
                        {formatRuDate(w.work_date)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          {w.executor_name ?? 'Исполнитель не указан'}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {[w.machine_name_text, w.comment].filter(Boolean).join(' · ') ||
                            '—'}
                        </div>
                      </div>
                      <span className="shrink-0 text-sm tabular-nums">
                        {w.area_ha.toFixed(1)} га
                      </span>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Править работу"
                          onClick={() => setWorkDialog({ open: true, work: w })}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Удалить работу"
                          onClick={() => archiveWork.mutate(w.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {archiveWork.isError && (
                <Alert variant="destructive">
                  <AlertDescription>{archiveWork.error?.message}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </PageWithRail>
      </div>

      <OperationFormDialog
        open={editOpen}
        operation={op}
        opTypes={opTypes ?? []}
        saving={updateOp.isPending}
        error={updateOp.error?.message ?? null}
        onSubmit={(body) => updateOp.mutate(body, { onSuccess: () => setEditOpen(false) })}
        onClose={() => setEditOpen(false)}
      />

      <WorkFormDialog
        open={workDialog.open}
        work={workDialog.work}
        defaultAreaHa={op.planned_area_ha}
        saving={workSaving}
        error={workError}
        onSubmit={submitWork}
        onClose={() => setWorkDialog({ open: false, work: null })}
      />
    </AppShell>
  );
}

export default OperationDetailPage;
